<template>
    <vx-expandable-card>
        <template v-slot:header>
            Address
        </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">Address Line 1</vx-label>

                    <vx-input
                        data-test="address-input"
                        v-model="group.address"
                        size="lg"
                        :error="props.errors.address"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md" class="w-full">
                        <div class="flex items-center justify-between flex-grow">
                            <span>Address Line 2</span>
                            <vx-badge color="slate" size="xs">
                                <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                            </vx-badge>
                        </div>
                    </vx-label>

                    <vx-input
                        data-test="address-line-2-input"
                        v-model="group.address_line_2"
                        size="lg"
                        :error="props.errors.address_line_2"
                    ></vx-input>
                </div>

                <div data-test="country-select">
                    <vx-label size="md">Country</vx-label>
                    <vx-selectable
                        v-model="group.country"
                        :options="countries"
                        :reduce="(option) => option.value"
                        size="lg"
                        :clearable="true"
                        :error="props.errors.country"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">City</vx-label>

                    <vx-input
                        data-test="city-input"
                        v-model="group.city"
                        size="lg"
                        :error="props.errors.city"
                    ></vx-input>
                </div>

                <div data-test="state-select">
                    <vx-label size="md">State</vx-label>

                    <vx-selectable
                        v-if="showStateDropdown"
                        v-model="group.state"
                        :options="states[group.country || 'US']"
                        :reduce="(option) => option.value"
                        size="lg"
                        :clearable="true"
                        :error="props.errors.state"
                        @update:modelValue="onStatePicked"
                    ></vx-selectable>
                    <vx-input
                        v-else
                        data-test="state-input"
                        v-model="group.state"
                        size="lg"
                        :error="props.errors.state"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Zip</vx-label>

                    <vx-input
                        data-test="zip-input"
                        v-model="group.zip"
                        size="lg"
                        :error="props.errors.zip"
                    ></vx-input>
                </div>

                <div data-test="time-zone-select">
                    <vx-label size="md">Time Zone</vx-label>

                    <vx-selectable
                        v-model="timeZone"
                        :options="timeZonesList"
                        size="lg"
                        :reduce="option => option.value"
                        placeholder="Select a time zone"
                        :clearable="true"
                        :error="props.timeZoneError"
                    ></vx-selectable>

                    <div class="mt-2">
                        <vx-button
                            data-test="derive-timezone-button"
                            @click="deriveNow"
                            size="sm"
                            color="muted"
                        >
                            Derive time zone from address
                        </vx-button>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxBadge, VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { computed } from 'vue';
import { countries, states } from '~/constants/countries';
import timeZones from '~/utils/timeZones';
import { timezoneFromZipCode, timezoneFromCanadianProvince } from '~/utils/zipToTimezone';
import { useToasted } from '~/composables/useToasted';
import { useAddressStateSelector } from '~/composables/useAddressStateSelector';

const toasted = useToasted();

const props = defineProps({
    errors: {
        type: Object,
        default: () => ({}),
    },
    timeZoneError: {
        type: [String, Array],
    },
});

const group = defineModel({
    required: true,
    type: Object,
});

const timeZone = defineModel('timeZone', {
    type: String,
    default: null,
});

const timeZonesList = computed(() => timeZones.map(timeZone => ({
    value: timeZone,
    label: timeZone.split('_').join(' '),
})));

const getDerivedTimezone = () => {
    const { country, state, zip } = group.value;

    if ('US' === country && zip && 5 === zip.length) {
        return timezoneFromZipCode(zip);
    }

    if ('CA' === country && state) {
        return timezoneFromCanadianProvince(state);
    }

    return null;
};

const deriveNow = () => {
    const tz = getDerivedTimezone();
    if (tz === null) {
        toasted.error('Could not derive time zone. Set country and a valid ZIP (US) or state (Canada).');
        return;
    }
    timeZone.value = tz;
};

const { showStateDropdown, onStatePicked } = useAddressStateSelector(group);
</script>
